import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Newspaper, ExternalLink, Clock, RefreshCw, AlertCircle } from "lucide-react";

interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  thumbnail: string;
  source: string;
}

const RSS_FEEDS = [
  {
    url: "https://timesofsandiego.com/category/life/environment/feed/",
    source: "Times of San Diego",
  },
  {
    url: "https://voiceofsandiego.org/feed/",
    source: "Voice of San Diego",
  },
  {
    url: "https://www.surfer.com/feed/",
    source: "Surfer Magazine",
  },
];

const RSS2JSON_BASE = "https://api.rss2json.com/v1/api.json?rss_url=";

const stripHtml = (html: string) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const timeAgo = (dateString: string) => {
  const now = new Date();
  const then = new Date(dateString);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const CoastalNewsSection = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchNews = async () => {
    setLoading(true);
    setError(false);
    try {
      const results = await Promise.allSettled(
        RSS_FEEDS.map(async (feed) => {
          const res = await fetch(`${RSS2JSON_BASE}${encodeURIComponent(feed.url)}`);
          const data = await res.json();
          if (data.status !== "ok") return [];
          return (data.items || []).slice(0, 4).map((item: any) => ({
            title: stripHtml(item.title),
            link: item.link,
            pubDate: item.pubDate,
            description: stripHtml(item.description).slice(0, 160),
            thumbnail: item.thumbnail || item.enclosure?.link || "",
            source: feed.source,
          }));
        })
      );

      const allNews = results
        .filter((r): r is PromiseFulfilledResult<NewsItem[]> => r.status === "fulfilled")
        .flatMap((r) => r.value)
        .sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime())
        .slice(0, 8);

      setNews(allNews);
      if (allNews.length === 0) setError(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="news" className="bg-background py-28 px-6">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground"
            >
              Stay Informed
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display mt-3 text-4xl font-semibold text-foreground sm:text-5xl"
            >
              Coastal <span className="italic">News</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-body mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground"
            >
              Live headlines from surf reports, coastal conservation, and San Diego shoreline happenings.
            </motion.p>
          </div>

          <motion.button
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            onClick={fetchNews}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-secondary px-4 py-2 font-body text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </motion.button>
        </div>

        <div className="mt-16">
          {loading && news.length === 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-xl border border-border bg-card p-5">
                  <div className="h-3 w-20 rounded bg-muted" />
                  <div className="mt-3 h-5 w-3/4 rounded bg-muted" />
                  <div className="mt-3 h-3 w-full rounded bg-muted" />
                  <div className="mt-2 h-3 w-2/3 rounded bg-muted" />
                </div>
              ))}
            </div>
          )}

          {error && news.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-16 text-center">
              <AlertCircle className="h-8 w-8 text-muted-foreground" />
              <p className="font-body mt-4 text-sm text-muted-foreground">
                Unable to load news right now. Please try again.
              </p>
              <button
                onClick={fetchNews}
                className="mt-4 rounded-lg bg-primary/10 px-4 py-2 font-body text-xs font-semibold uppercase tracking-wider text-primary transition-colors hover:bg-primary/20"
              >
                Retry
              </button>
            </div>
          )}

          {news.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {news.map((item, i) => {
                const text = `${item.title} ${item.description}`.toLowerCase();
                const isWaterQuality = /water quality|sewage|advisor|closure|bacteria|spill|runoff|tijuana/.test(text);
                const isConservation = /conserv|kelp|cleanup|surfrider|coastkeeper|restoration|marine protect|wildlife/.test(text);
                const anchorId = isWaterQuality ? "water-quality" : isConservation ? "conservation" : undefined;
                return (
                <motion.a
                  key={item.link + i}
                  id={anchorId}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col scroll-mt-24 rounded-xl border border-border bg-card p-5 transition-all duration-300 hover:shadow-lg hover:border-primary/30"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: 0.06 * i }}
                >
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-body text-[10px] font-semibold uppercase tracking-wider text-primary">
                      {item.source}
                    </span>
                    <span className="flex items-center gap-1 font-body text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {timeAgo(item.pubDate)}
                    </span>
                  </div>

                  <h3 className="font-display mt-3 text-base font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {item.title}
                  </h3>

                  {item.description && (
                    <p className="font-body mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="mt-auto pt-4 flex items-center gap-1.5 font-body text-xs font-semibold uppercase tracking-wider text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Read Article
                    <ExternalLink className="h-3 w-3" />
                  </div>
                </motion.a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CoastalNewsSection;
